/* ===========================================================
   ORDER CONFIRMATION — reads ?order=NS-... via useOrder()
   =========================================================== */
import React from "react";
import { ProductTile } from "@/components/product-card";
import { EmptyState } from "@/components/EmptyState";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { getQuery, toast } from "@/components/shell";
import { inr } from "@/data/data";
import { Icons } from "@/lib/icons";
import { useOrder } from "@/api/hooks";
import { downloadInvoice } from "@/lib/invoice";

function ConfirmationPage() {
  const number = getQuery("order") || getQuery("id");
  const { data: order, isLoading, error } = useOrder(number);

  if (!number) {
    return (
      <div className="container" style={{ padding: "48px 0" }}>
        <EmptyState
          title="No order to display"
          description="Looks like you arrived here directly. Start by browsing the catalog."
          cta={{ label: "Browse products", href: "/pages/storefront/products.html" }}
        />
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="container" style={{ padding: "48px 0" }}>
        <LoadingSkeleton rows={6} height={48} />
      </div>
    );
  }
  if (error || !order) {
    return (
      <div className="container" style={{ padding: "48px 0" }}>
        <EmptyState
          title="Order not found"
          description={`We couldn't find order ${number}. It may have been removed or the link is incorrect.`}
          cta={{ label: "Back to home", href: "/" }}
        />
      </div>
    );
  }

  const addr = order.address;
  const status = order.status;

  return (
    <>
      <section className="confirm-hero">
        <div className="container">
          <div className="confirm-check">
            <Icons.Check size={42} stroke={2.6} />
          </div>
          <h1 className="confirm-title">Order placed. Thank you.</h1>
          <p className="confirm-sub">
            We've sent a confirmation to your registered email. Your order is being packed at Palayamkottai · Tirunelveli and will be dispatched within the hour.
          </p>
          <div className="confirm-meta">
            <div>
              <div className="confirm-meta-k">ORDER ID</div>
              <div className="confirm-meta-v">{order.number}</div>
            </div>
            <div style={{ width: 1, height: 28, background: "var(--line)" }} />
            <div>
              <div className="confirm-meta-k">ETA</div>
              <div className="confirm-meta-v">{order.eta}</div>
            </div>
            <div style={{ width: 1, height: 28, background: "var(--line)" }} />
            <div>
              <div className="confirm-meta-k">TOTAL</div>
              <div className="confirm-meta-v">{inr(order.total)}</div>
            </div>
          </div>
          <div style={{ marginTop: 24, display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <a href={"/pages/storefront/track-order.html?id=" + encodeURIComponent(order.number)} className="btn btn-primary">
              Track this order <Icons.ArrowR size={14} stroke={2} />
            </a>
            <a href="/pages/storefront/products.html" className="btn btn-outline">Continue shopping</a>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="confirm-grid">
          <div className="confirm-card">
            <div className="confirm-card-h">Delivery timeline</div>
            <div className="confirm-timeline">
              {order.timeline.map((t, i) => (
                <div key={i} className="confirm-tl-row">
                  <div className="confirm-tl-marker">
                    <div className={"confirm-tl-dot" + (t.done ? " is-done" : (t.code === status ? " is-current" : ""))} />
                    {i < order.timeline.length - 1 && <div className="confirm-tl-line" />}
                  </div>
                  <div className="confirm-tl-body">
                    <div className="confirm-tl-label">{t.label}</div>
                    <div className="confirm-tl-meta">
                      {new Date(t.ts).toLocaleString("en-IN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      {t.note ? " · " + t.note : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="confirm-card">
            <div className="confirm-card-h">Order details</div>

            <div className="kicker" style={{ marginBottom: 6 }}>Ship to</div>
            <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--ink-2)", paddingBottom: 14, borderBottom: "1px dashed var(--line)" }}>
              <div style={{ fontWeight: 500, color: "var(--ink)" }}>{addr.full_name}</div>
              <div>{addr.line1}{addr.line2 ? ", " + addr.line2 : ""}</div>
              <div>{addr.city} {addr.pin}, {addr.state}</div>
              <div className="mono" style={{ marginTop: 4 }}>{addr.phone}</div>
            </div>

            <div className="kicker" style={{ margin: "16px 0 6px" }}>Items</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {order.items.map((it) => (
                <div key={it.product_slug} style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <div style={{ width: 40, height: 40 }}>
                    <ProductTile product={{ name: it.product_name, slug: it.product_slug }} size="sm" />
                  </div>
                  <div style={{ flex: 1, fontSize: 13 }}>
                    <div style={{ fontWeight: 500 }}>{it.product_name}</div>
                    <div style={{ color: "var(--ink-3)", fontSize: 12 }}>Qty {it.qty}</div>
                  </div>
                  <div className="mono" style={{ fontWeight: 500, fontSize: 13 }}>{inr(it.line_total)}</div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px dashed var(--line)", fontSize: 13.5 }}>
              <div className="cart-summary-row"><span>Subtotal</span><span className="mono">{inr(order.subtotal)}</span></div>
              {order.discount > 0 && (
                <div className="cart-summary-row"><span>Discount{order.coupon_code ? " (" + order.coupon_code + ")" : ""}</span><span className="mono">−{inr(order.discount)}</span></div>
              )}
              <div className="cart-summary-row"><span>GST</span><span className="mono">{inr(order.gst_amount)}</span></div>
              <div className="cart-summary-row"><span>Shipping</span><span className="mono">{order.shipping === 0 ? "FREE" : inr(order.shipping)}</span></div>
              <div className="cart-summary-row total"><span>Total</span><span className="mono">{inr(order.total)}</span></div>
            </div>

            <div className="kicker" style={{ margin: "16px 0 6px" }}>Payment</div>
            <div style={{ fontSize: 13, color: "var(--ink-2)", textTransform: "uppercase" }}>
              {order.payment_method} · {order.payment_status}
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
              <button
                className="btn btn-outline btn-sm"
                style={{ flex: 1, justifyContent: "center" }}
                onClick={() => { downloadInvoice(order); toast && toast("Invoice downloaded"); }}
              >
                <Icons.Download size={13} stroke={2} /> Invoice
              </button>
              <button
                className="btn btn-outline btn-sm"
                style={{ flex: 1, justifyContent: "center" }}
                onClick={() => toast && toast("Email sent (mock)")}
              >
                <Icons.Mail size={13} stroke={2} /> Email me
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export { ConfirmationPage };
